<template>
  <div v-bind="$attrs" class="rounded-md border border-line p-12 shadow-md">
    <div class="mb-8 flex items-center justify-between">
      <div class="flex items-center gap-8">
        <span
          class="flex h-32 w-32 items-center justify-center rounded-full border border-line"
        >
          <JIcon name="location" size="14" class="text-gray-500" />
        </span>

        <span class="text-subject font-medium text-dark">
          {{ hotel.city }}, {{ hotel.country }}
        </span>
      </div>

      <JButton
        icon="trash-bin"
        color="danger"
        variant="tonal"
        size="xs"
        class="rounded-full"
        @click.stop="setIsOpen(true)"
      />
    </div>

    <div class="mb-8 flex items-center gap-12">
      <JImage
        :src="hotelImage"
        :alt="hotel.name"
        containerClass="w-[64px] h-[64px] aspect-video rounded-sm"
        class="object-cover"
      />

      <div class="flex flex-1 flex-col">
        <div class="flex items-center justify-between">
          <strong class="text-pane-header text-dark">{{ hotel.name }}</strong>

          <JRating :modelValue="hotel.hotelRate" :size="12" class="!gap-x-4" />
        </div>

        <div>
          <JTooltip
            placement="top-start"
            arrow
            hover
            offsetDistance="5"
            arrowPadding="85"
          >
            <template #activator>
              <span class="text-subject font-medium text-gray-600">
                {{
                  $t('search.itineraryHotelCardPassengers', {
                    count: hotel.rooms.length,
                    guests: $t('search.nGuests', { count: guests }),
                  })
                }}
              </span>
            </template>

            <template #content>
              <div
                class="min-w-[18rem] rounded-sm border border-line p-12 text-center text-metadata-limited font-bold shadow"
              >
                <p
                  v-for="(
                    { quantity, description }, index
                  ) in baggageAllowances"
                  :key="index"
                >
                  {{ quantity }} x {{ description }}
                </p>
              </div>
            </template>
          </JTooltip>

          <span class="mx-4 text-subject font-medium text-gray-300">|</span>

          <div
            class="inline-flex cursor-pointer text-subject font-medium text-primary underline"
            @click="showDetails"
          >
            <span> {{ $t('search.changeYourSelection') }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="flex flex-wrap items-center gap-8 text-gray-600">
      <span class="rounded-sm bg-gray-200 px-4">
        {{ $t('checkIn') }}
        <time class="font-medium">{{ checkInDate }} </time>
      </span>
      <span class="rounded-sm bg-gray-200 px-4">
        {{ $t('checkOut') }}
        <time class="font-medium">{{ checkOutDate }} </time>
      </span>
    </div>
  </div>

  <JConfirmation
    :modelValue="isOpen"
    :title="$t('search.areYouSureYouWantToRemoveThisItem')"
    color="danger"
    :confirmText="$t('words.remove')"
    :cancelText="$t('words.cancel')"
    @update:model-value="setIsOpen"
    @confirm="onDelete"
    @cancel="setIsOpen(false)"
  />
</template>

<script setup lang="ts">
import { useToggle } from '@vueuse/core'
import {
  JButton,
  JConfirmation,
  JIcon,
  JImage,
  JRating,
  JTooltip,
} from '@journey/ui-kit'
import { computed, inject } from 'vue'
import type { Props } from './ItinerarySummaryHotelCardContent.types'
import { HotelImageSize } from '@/modules/Search/types'
import useItinerarySummary from '@/modules/Search/store/itinerarySummary'
import { useItinerary } from '@/modules/Search/composables'
import { showHotelDetailsInjectionKey } from '@/modules/Search/constants'
import {
  getHotelCheckinDateTime,
  getHotelCheckoutDateTime,
} from '@/modules/Core/utils/hotelCheckTime'

defineOptions({
  inheritAttrs: false,
})

const props = defineProps<Props>()

const [isOpen, setIsOpen] = useToggle(false)

const itinerarySummaryStore = useItinerarySummary()

const { submitItinerary } = useItinerary()
const showHotelDetail = inject(showHotelDetailsInjectionKey)

function showDetails() {
  if (!showHotelDetail) {
    return
  }

  showHotelDetail.showHotelDetail(props.hotel)
}

async function onDelete() {
  itinerarySummaryStore.remove(props.hotel.uniqueId)

  const { data } = await submitItinerary()

  itinerarySummaryStore.setItineraries(data.data.itinerary)

  setIsOpen(false)
}

const hotelImage = computed(() => {
  const { images } = props.hotel
  if (!images.length) {
    return ''
  }

  const biggerImage = images[0].pathList.find(
    (img) => img.size === HotelImageSize.Bigger
  )?.address

  const mediumImage = images[0].pathList.find(
    (img) => img.size === HotelImageSize.Medium
  )?.address

  return biggerImage || mediumImage || ''
})

const guests = computed(() =>
  props.hotel.rooms
    .map((room) => (room?.maxAdults || 0) + (room?.maxChildren || 0))
    .reduce((acc, curr) => acc + curr, 0)
)

const checkInDate = computed(() => {
  return getHotelCheckinDateTime(
    props.hotel.checkIn,
    props.hotel.rooms?.[0]?.rateComments
  )
})

const checkOutDate = computed(() => {
  return getHotelCheckoutDateTime(
    props.hotel.checkOut,
    props.hotel.rooms?.[0]?.rateComments
  )
})

const baggageAllowances = computed(() =>
  props.hotel.rooms.map(({ quantity, description }) => ({
    quantity,
    description,
  }))
)
</script>
